// src/components/popups/RequestConsultationModal.js
import React, {useState, useCallback, useEffect, useRef} from 'react';
import ReactDOM from 'react-dom';
import InputWithLabel from "../Form/Input/InputWithLabel";
import api from '../../api';
import {useUser} from '../../context/UserContext';
import fetchLocationByIP from '../../utils/fetchLocationByIP';

export default function RequestConsultationModal({show, recipient, recipientType, onClose}) {
    const {user, isAuthenticated} = useUser();
    const [formData, setFormData] = useState({
        name: '',
        phone_number: '',
        email: '', // Added email field
        state_id: '',
        city_id: '',
        specialization: '',
    });

    const [detectedIpLocation, setDetectedIpLocation] = useState(null);
    const [isIpDetectionDone, setIsIpDetectionDone] = useState(false);

    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [specializations, setSpecializations] = useState([]);

    const [loadingIpLocation, setLoadingIpLocation] = useState(false);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [errors, setErrors] = useState({});
    const [requestSent, setRequestSent] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const initialModalOpenSequenceDone = useRef(false);


    const resetFormAndState = useCallback((isOpening = false) => {
        setFormData(prev => ({
            name: isAuthenticated && user ? (user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '') : '',
            phone_number: isAuthenticated && user ? (user.phone_number || user.phone || '') : '',
            email: isAuthenticated && user ? (user.email || '') : '', // Set email if authenticated
            state_id: '',
            city_id: '',
            specialization: prev.specialization,
        }));
        setErrors({});
        setSubmitError(null);

        if (!isOpening) {
            setRequestSent(false);
            setIsSubmitting(false);
            setCities([]);
            initialModalOpenSequenceDone.current = false;
            setSpecializations(prevSpecs => prevSpecs.length > 0 ? [{
                value: '',
                label: 'Select a Specialization*'
            }, ...prevSpecs.slice(1)] : []);
        } else {
            setRequestSent(false);
            setIsSubmitting(false);
        }
    }, [isAuthenticated, user]);

    useEffect(() => {
        if (show) {
            resetFormAndState(true);

            if (!initialModalOpenSequenceDone.current) {
                setLoadingIpLocation(true);
                fetchLocationByIP().then(ipData => {
                    if (ipData) {
                        setDetectedIpLocation(ipData);
                    }
                }).catch(err => console.error("Failed to get IP location", err))
                    .finally(() => {
                        setLoadingIpLocation(false);
                        setIsIpDetectionDone(true);
                    });

                setLoadingStates(true);
                api.get(`/states-filtered/?country_id=1`)
                    .then(response => {
                        setStates([{state_id: '', name: 'Select State*'}, ...response.data]);
                    })
                    .catch(error => {
                        console.error('Error fetching states:', error);
                        setStates([{state_id: '', name: 'Could not load states'}]);
                        setErrors(prev => ({...prev, state_id: 'Could not load states.'}));
                    })
                    .finally(() => setLoadingStates(false));

                initialModalOpenSequenceDone.current = true;
            }

            api.get(recipientType === 'legalcompany' && recipient?.id ? `/company/${recipient.id}/specializations/`
                : recipientType === 'attorney' && recipient?.attorney_id ? `/attorney/${recipient.attorney_id}/specializations/`
                    : '/get-specialization/')
                .then(response => {
                    const transformedSpecializations = response.data.map(spec => ({
                        value: spec.specialization_id || spec.id || spec.name,
                        label: spec.name,
                    }));
                    setSpecializations([{value: '', label: 'Select a Specialization*'}, ...transformedSpecializations]);
                })
                .catch(error => {
                    console.error('Error loading specializations:', error);
                    setSpecializations([{value: '', label: 'Could not load specializations'}]);
                });

            if (isAuthenticated && user) {
                setFormData(prevData => ({
                    ...prevData,
                    name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '',
                    phone_number: user.phone_number || user.phone || '',
                    email: user.email || '', // Populate email if authenticated
                }));
            }

        } else {
            const timer = setTimeout(() => {
                resetFormAndState(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [show, isAuthenticated, user, recipient, recipientType, resetFormAndState]);


    useEffect(() => {
        if (isIpDetectionDone && detectedIpLocation && states.length > 1 && !formData.state_id && !loadingStates) {
            const matchedState = states.find(s => s.name && detectedIpLocation.region && s.name.toLowerCase() === detectedIpLocation.region.toLowerCase());
            if (matchedState && matchedState.state_id) {
                setFormData(prev => ({...prev, state_id: matchedState.state_id}));
            }
        }
    }, [isIpDetectionDone, detectedIpLocation, states, formData.state_id, loadingStates]);


    useEffect(() => {
        if (formData.state_id) {
            setLoadingCities(true);
            setCities([]);
            setFormData(prev => ({...prev, city_id: ''}));

            api.get(`/cities-filtered/?state_id=${formData.state_id}`)
                .then(response => {
                    const fetchedCities = response.data;
                    setCities([{city_id: '', name: 'Select City*'}, ...fetchedCities]);

                    if (detectedIpLocation && states.find(s => s.state_id === formData.state_id && s.name && detectedIpLocation.region && s.name.toLowerCase() === detectedIpLocation.region.toLowerCase())) {
                        const matchedCity = fetchedCities.find(c => c.name && detectedIpLocation.city && c.name.toLowerCase() === detectedIpLocation.city.toLowerCase());
                        if (matchedCity && matchedCity.city_id) {
                            setFormData(prev => ({...prev, city_id: matchedCity.city_id}));
                        }
                    }
                })
                .catch(error => {
                    console.error('Error fetching cities:', error);
                    setCities([{city_id: '', name: 'Could not load cities'}]);
                    setErrors(prev => ({...prev, city_id: 'Could not load cities for this state.'}));
                })
                .finally(() => setLoadingCities(false));
        } else {
            setCities([]);
            if (formData.city_id) setFormData(prev => ({...prev, city_id: ''}));
        }
    }, [formData.state_id, detectedIpLocation, states]);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: null}));
        }
        if (name === 'state_id') {
            setFormData(prevData => ({...prevData, city_id: ''}));
            setCities([]);
        }
        setSubmitError(null);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        // Email validation only if user is not authenticated
        if (!isAuthenticated) {
            if (!formData.email.trim()) {
                newErrors.email = 'Email is required';
            } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
                newErrors.email = 'Email address is invalid';
            }
        }
        if (!formData.state_id) newErrors.state_id = 'State is required';
        if (!formData.city_id) newErrors.city_id = 'City is required';
        if (!formData.specialization) newErrors.specialization = 'Please select a specialization';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);
        if (!validateForm() || isSubmitting) return;
        setIsSubmitting(true);

        const dataToSend = {
            name: formData.name,
            phone_number: formData.phone_number,
            state_id: formData.state_id,
            city_id: formData.city_id,
            specialization_id: formData.specialization,
            recipient_id: recipientType === 'attorney' ? recipient.attorney_id : recipient.id,
            recipient_type: recipientType,
        };
        // Add email to dataToSend only if user is not authenticated
        if (!isAuthenticated) {
            dataToSend.email = formData.email;
        }


        try {
            const response = await api.post('/request-consultation/', dataToSend);
            if (response.status === 201 || response.status === 200 || response.data?.status?.includes('requested')) {
                setRequestSent(true);
            } else {
                setSubmitError(response.data?.message || 'An unexpected response occurred.');
            }
        } catch (error) {
            const errorData = error.response?.data;
            const errorMsg = errorData?.message || errorData?.detail || (typeof errorData === 'string' ? errorData : null) || 'Failed to submit request.';
            setSubmitError(errorMsg);
            if (errorData?.errors && typeof errorData.errors === 'object') {
                setErrors(prev => ({...prev, ...errorData.errors}));
            } else if (errorData && typeof errorData === 'object' && !errorData.errors) {
                const fieldSpecificErrors = {};
                for (const key in errorData) {
                    if (formData.hasOwnProperty(key) || `${key}_id` in formData) {
                        fieldSpecificErrors[key] = Array.isArray(errorData[key]) ? errorData[key].join(' ') : errorData[key];
                    }
                }
                if (Object.keys(fieldSpecificErrors).length > 0) {
                    setErrors(prev => ({...prev, ...fieldSpecificErrors}));
                }
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = useCallback(() => {
        if (!isSubmitting) {
            onClose();
        }
    }, [onClose, isSubmitting]);


    if (!show) return null;

    const commonSelectStyle = {
        width: '100%',
        padding: '10px',
        marginBottom: '0px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxSizing: 'border-box',
        appearance: 'none',
        background: 'white url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'%3e%3cpath fill=\'none\' stroke=\'%23333\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M2 5l6 6 6-6\'/%3e%3c/svg%3e") no-repeat right .75rem center/16px 12px'
    };
    const errorSelectStyle = {...commonSelectStyle, borderColor: 'red'};
    const errorSpanStyle = {
        color: 'red',
        fontSize: '0.875em',
        marginTop: '2px',
        display: 'block',
        marginBottom: '10px'
    };

    return ReactDOM.createPortal(
        <div className={`modal ${show ? 'visible' : ''}`}>
            <div className="modal__overlay" onClick={handleClose}></div>
            <div className="modal__content">
                <button onClick={handleClose} className="button no-style close js--close-modal" aria-label="Close modal"
                        disabled={isSubmitting}>
                    <img src="/assets/icon--close-CN6QlgAA.svg" alt="Close"/>
                </button>
                <div className="modal__form">
                    {requestSent ? (
                        <div className="content text-center">
                            <img src="/assets/icon--sendok.svg" alt="Success"
                                 style={{width: '80px', marginBottom: '20px'}}/>
                            <h2>Consultation Requested</h2>
                            <p>Your request has been sent successfully. {recipient?.name || 'They'} will contact you
                                soon.</p>
                            <button type='button' onClick={handleClose} className="button button-black">Okay</button>
                        </div>
                    ) : (
                        <form className="content" onSubmit={handleSubmit} noValidate>
                            <h2>Request a Legal Help</h2>
                            <p>Please provide your details for a Legal Help.</p>
                            {submitError && <div className="error-message general-error" style={{
                                color: 'red',
                                marginBottom: '10px',
                                border: '1px solid red',
                                padding: '8px',
                                borderRadius: '4px'
                            }}>{submitError}</div>}

                            <InputWithLabel name="name" label="Your Name*" value={formData.name} onChange={handleChange}
                                            error={errors.name} required/>
                            <InputWithLabel name="phone_number" label="Your Phone Number (Optional)" type="tel"
                                            value={formData.phone_number} onChange={handleChange}
                                            error={errors.phone_number}/>
                            {!isAuthenticated && (
                                <InputWithLabel name="email" label="Your Email*" type="email"
                                                value={formData.email} onChange={handleChange}
                                                error={errors.email} required/>
                            )}

                            <label htmlFor="state_id_select"
                                   className="label">State* {loadingIpLocation ? "(Detecting...)" : loadingStates ? "(Loading...)" : ""}</label>
                            <select
                                id="state_id_select"
                                name="state_id"
                                value={formData.state_id}
                                onChange={handleChange}
                                style={errors.state_id ? errorSelectStyle : commonSelectStyle}
                                disabled={loadingStates || loadingIpLocation || states.length <= 1 && !loadingStates}
                                required
                            >
                                {states.map((state) => <option key={state.state_id || `state-${state.name}`}
                                                               value={state.state_id}>{state.name}</option>)}
                            </select>
                            {errors.state_id &&
                                <span className="error-message" style={errorSpanStyle}>{errors.state_id}</span>}

                            <label htmlFor="city_id_select"
                                   className="label">City* {loadingCities ? "(Loading...)" : ""}</label>
                            <select
                                id="city_id_select"
                                name="city_id"
                                value={formData.city_id}
                                onChange={handleChange}
                                style={errors.city_id ? errorSelectStyle : commonSelectStyle}
                                disabled={!formData.state_id || loadingCities || (cities.length <= 1 && !loadingCities)}
                                required
                            >
                                {cities.map((city) => <option key={city.city_id || `city-${city.name}`}
                                                              value={city.city_id}>{city.name}</option>)}
                            </select>
                            {errors.city_id &&
                                <span className="error-message" style={errorSpanStyle}>{errors.city_id}</span>}
                            {!formData.state_id && cities.length === 0 &&
                                <span style={{fontSize: '0.8em', color: '#777', display: 'block', marginTop: '2px'}}>Please select a state first.</span>}

                            <label htmlFor="specialization_select" className="label">Specialization*</label>
                            <select
                                id="specialization_select"
                                name="specialization"
                                value={formData.specialization}
                                onChange={handleChange}
                                style={errors.specialization ? errorSelectStyle : commonSelectStyle}
                                required
                            >
                                {specializations.map((spec) => <option key={spec.value || spec.label}
                                                                       value={spec.value}>{spec.label}</option>)}
                            </select>
                            {errors.specialization &&
                                <span className="error-message" style={errorSpanStyle}>{errors.specialization}</span>}

                            <button type="submit" className="button button-black"
                                    disabled={isSubmitting || loadingStates || loadingCities || loadingIpLocation}>
                                {isSubmitting ? 'Submitting...' : 'Request Legal Help'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}